import React, { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { ActionFormData } from "./validation.schema";
import { ActionStatusEnum, ActionTypeEnum } from "@/domain/action.enum";
import { Action } from "@/domain/action.interface";

export interface ActionModalPresenterProps {
  open: boolean;
  mode: "create" | "edit";
  isSubmitting: boolean;
  isLoadingActionData: boolean;
  onClose: () => void;
  onSubmit: () => void;
  allActions?: Action[];
  isLoadingAllActions?: boolean;
}

export const ActionModalPresenter: FC<ActionModalPresenterProps> = ({
  open,
  mode,
  isSubmitting,
  isLoadingActionData,
  onClose,
  onSubmit,
  allActions = [],
  isLoadingAllActions = false,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useFormContext<ActionFormData>();

  const title = mode === "create" ? "Create Action" : "Edit Action";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="headlineSmall">{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadingActionData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 1,
            }}
          >
            {/* Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  required
                />
              )}
            />

            {/* Code */}
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Code"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  required
                />
              )}
            />

            {/* Description */}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  required
                />
              )}
            />

            {/* Type */}
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  fullWidth
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  required
                >
                  {Object.values(ActionTypeEnum).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Status"
                  fullWidth
                  error={!!errors.status}
                  helperText={errors.status?.message}
                  required
                >
                  {Object.values(ActionStatusEnum).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Link */}
            <Controller
              name="link"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Link"
                  fullWidth
                  error={!!errors.link}
                  helperText={errors.link?.message}
                />
              )}
            />

            {/* Order */}
            <Controller
              name="order"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Order"
                  type="number"
                  fullWidth
                  error={!!errors.order}
                  helperText={errors.order?.message}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />

            {/* Icon */}
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Icon"
                  fullWidth
                  error={!!errors.icon}
                  helperText={errors.icon?.message}
                />
              )}
            />

            {/* Sub Actions */}
            <Controller
              name="subActions"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="subactions-label">Sub Actions</InputLabel>
                  <Select
                    {...field}
                    labelId="subactions-label"
                    multiple
                    value={field.value || []}
                    onChange={(e) => field.onChange(e.target.value)}
                    input={<OutlinedInput label="Sub Actions" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(selected as string[]).map((value) => {
                          const action = allActions.find(
                            (a) => a._id === value,
                          );
                          return (
                            <Chip
                              key={value}
                              label={action?.name || value}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                    disabled={isLoadingAllActions}
                  >
                    {isLoadingAllActions ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading...
                      </MenuItem>
                    ) : (
                      allActions.map((action) => (
                        <MenuItem key={action._id} value={action._id}>
                          {action.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting || isLoadingActionData}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : mode === "create" ? (
              "Create"
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
